type Wave = OscillatorType;

class ArcadeAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private muted = false;
  private music: HTMLAudioElement | null = null;

  setMuted(value: boolean) {
    this.muted = value;
    if (this.master) this.master.gain.value = value ? 0 : 0.16;
    if (this.music) this.music.muted = value;
  }

  isMuted() {
    return this.muted;
  }

  private ensureContext() {
    if (!this.context) {
      this.context = new AudioContext();
      this.master = this.context.createGain();
      this.master.gain.value = this.muted ? 0 : 0.16;
      this.master.connect(this.context.destination);
    }
    if (this.context.state === 'suspended') void this.context.resume();
    return this.context;
  }

  tone(frequency = 440, duration = 0.08, wave: Wave = 'square', volume = 0.22, slide = 0) {
    if (this.muted) return;
    const context = this.ensureContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const now = context.currentTime;
    oscillator.type = wave;
    oscillator.frequency.setValueAtTime(frequency, now);
    if (slide) oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, frequency + slide), now + duration);
    gain.gain.setValueAtTime(volume, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    oscillator.connect(gain);
    gain.connect(this.master as GainNode);
    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  cue(kind: 'click' | 'coin' | 'hit' | 'power' | 'lose' | 'win') {
    const cues: Record<typeof kind, [number, number, Wave, number, number]> = {
      click: [220, 0.045, 'square', 0.12, 80],
      coin: [740, 0.11, 'sine', 0.17, 520],
      hit: [110, 0.1, 'sawtooth', 0.2, -65],
      power: [330, 0.22, 'triangle', 0.2, 540],
      lose: [180, 0.42, 'sawtooth', 0.15, -120],
      win: [523, 0.4, 'triangle', 0.18, 524],
    };
    this.tone(...cues[kind]);
  }

  playMusic(source: string) {
    if (this.music?.dataset.arcadeSource === source) return;
    this.stopMusic();
    const probe = new Audio();
    const resolvedSource = source.endsWith('.ogg') && !probe.canPlayType('audio/ogg; codecs="vorbis"')
      ? source.replace(/\.ogg$/i, '.mp3')
      : source;
    const music = new Audio(resolvedSource);
    music.dataset.arcadeSource = source;
    music.loop = true;
    music.volume = 0.22;
    music.muted = this.muted;
    this.music = music;
    void music.play().catch(() => undefined);
  }

  pauseMusic() {
    this.music?.pause();
  }

  resumeMusic() {
    if (!this.music || this.muted) return;
    void this.music.play().catch(() => undefined);
  }

  stopMusic() {
    if (!this.music) return;
    this.music.pause();
    this.music.src = '';
    this.music = null;
  }
}

export const arcadeAudio = new ArcadeAudio();
